import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PresencaChamada } from '@arena/shared/dominio';
import { zChamada, type ChamadaDaAula, type LinhaDaChamada } from '@arena/shared/schemas';
import { horaNaArena, dataPorExtenso } from '../agenda/formatos.js';
import { useAtrasado } from '../agenda/useAtrasado.js';
import { ErroDeApi, ErroDeRede, mensagemDoErro } from '../api/cliente.js';
import { turmasApi } from '../api/turmas.js';
import { useSessao } from '../auth/SessaoContext.jsx';
import { Erro, EsqueletoDeCard } from '../ui/estados.jsx';
import { Icone } from '../ui/Icone.js';
import { CorpoDeModal, Modal } from '../ui/Modal.jsx';
import { Avatar, Botao, Vazio } from '../ui/primitivos.jsx';
import { useToast } from '../ui/Toast.jsx';
import { COR_DO_ESTADO, ROTULO_ESTADO_NA_CHAMADA } from './formatos.js';

/**
 * `#m-chamada` — Presente / Falta / Reposição por aluno, com o resumo ao
 * vivo. É a tela que o professor abre no CELULAR no fim da aula, e por
 * isso é estreita, com botões grandes e sem nada além do que se marca.
 *
 * Três regras que a tela ecoa e o servidor decide:
 *
 *   a lista é a do DIA    quem entrou na turma depois não aparece na aula
 *                         de antes — o servidor manda os matriculados da
 *                         data da aula
 *   refazer é salvar      mandar a chamada de novo atualiza; o que muda
 *                         nas reposições é estornado, não apagado
 *   chamada inteira       todo matriculado vai marcado: como no protótipo,
 *                         quem ainda não foi marcado começa "Presente", e o
 *                         professor toca só em quem faltou
 *
 * A falta pode ser JUSTIFICADA — é o que dá crédito de reposição. E quem
 * veio de outra turma repor entra por busca, gastando uma reposição.
 *
 * Ver: Arena_backend/Docs/plano/10-turmas-professores-chamada.md
 */

interface Marca {
  readonly estado: PresencaChamada;
  readonly justificada: boolean;
}

export function ModalDeChamada({
  marcacaoId,
  onFechar,
}: {
  marcacaoId: string;
  onFechar: () => void;
}) {
  const consulta = useQuery({
    queryKey: ['turmas', 'chamada', marcacaoId],
    queryFn: () => turmasApi.chamada(marcacaoId),
    retry: false,
  });

  if (consulta.isPending) {
    return (
      <Modal aberto onFechar={onFechar} titulo="Carregando a chamada…" largura="estreita">
        <CorpoDeModal>
          <EsqueletoDeCard linhas={5} />
        </CorpoDeModal>
      </Modal>
    );
  }

  if (consulta.isError) {
    const erro = consulta.error;
    return (
      <Modal aberto onFechar={onFechar} titulo="Chamada" largura="estreita">
        <CorpoDeModal>
          <Erro
            tipo={
              erro instanceof ErroDeRede
                ? 'rede'
                : erro instanceof ErroDeApi && erro.ehPermissao
                  ? 'permissao'
                  : 'falha'
            }
            detalhe={mensagemDoErro(erro)}
          />
        </CorpoDeModal>
      </Modal>
    );
  }

  return <ChamadaAberta chamada={consulta.data} onFechar={onFechar} />;
}

function marcasIniciais(chamada: ChamadaDaAula): Map<string, Marca> {
  const marcas = new Map<string, Marca>();
  for (const linha of chamada.alunos) {
    marcas.set(linha.aluno.id, {
      estado: linha.estado ?? 'Presente',
      justificada: linha.justificada,
    });
  }
  return marcas;
}

function ChamadaAberta({ chamada, onFechar }: { chamada: ChamadaDaAula; onFechar: () => void }) {
  const { pode } = useSessao();
  const avisar = useToast();
  const cliente = useQueryClient();

  const podeRegistrar = pode('turmas', 'registrar_chamada') && chamada.aberta && !chamada.cancelada;

  const [marcas, setMarcas] = useState(() => marcasIniciais(chamada));
  const [visitantes, setVisitantes] = useState<LinhaDaChamada['aluno'][]>(() =>
    chamada.visitantes.map((v) => v.aluno),
  );
  const [erro, setErro] = useState<string | null>(null);

  // A chamada recarregada (depois de salvar) redefine o que está marcado.
  useEffect(() => {
    setMarcas(marcasIniciais(chamada));
    setVisitantes(chamada.visitantes.map((v) => v.aluno));
  }, [chamada]);

  const salvar = useMutation({
    mutationFn: () => {
      const pedido = zChamada.parse({
        registros: [
          ...chamada.alunos.map((linha) => {
            const marca = marcas.get(linha.aluno.id) ?? { estado: 'Presente', justificada: false };
            return {
              alunoId: linha.aluno.id,
              estado: marca.estado,
              justificada: marca.estado === 'Falta' && marca.justificada,
            };
          }),
          ...visitantes.map((v) => ({ alunoId: v.id, estado: 'Reposicao', justificada: false })),
        ],
      });
      return turmasApi.registrarChamada(chamada.marcacaoId, pedido);
    },
    onSuccess: () => cliente.invalidateQueries({ queryKey: ['turmas'] }),
  });

  const marcar = (alunoId: string, estado: PresencaChamada) =>
    setMarcas((atuais) => {
      const novas = new Map(atuais);
      novas.set(alunoId, { estado, justificada: false });
      return novas;
    });

  const justificar = (alunoId: string, justificada: boolean) =>
    setMarcas((atuais) => {
      const novas = new Map(atuais);
      novas.set(alunoId, { estado: 'Falta', justificada });
      return novas;
    });

  const resumo = useMemo(() => {
    let presentes = 0;
    let faltas = 0;
    for (const linha of chamada.alunos) {
      const estado = marcas.get(linha.aluno.id)?.estado ?? 'Presente';
      if (estado === 'Falta') faltas += 1;
      else presentes += 1;
    }
    return { presentes, faltas, reposicoes: visitantes.length };
  }, [chamada.alunos, marcas, visitantes.length]);

  async function confirmar() {
    setErro(null);
    try {
      const resultado = await salvar.mutateAsync();
      const emRisco = resultado.entraramEmRisco.map((r) => r.nome);
      avisar(
        emRisco.length > 0
          ? `Chamada registrada. ${emRisco.join(', ')} ${emRisco.length === 1 ? 'entrou' : 'entraram'} em risco de cancelamento.`
          : chamada.registrada
            ? 'Chamada atualizada.'
            : 'Chamada registrada.',
      );
      onFechar();
    } catch (e) {
      setErro(mensagemDoErro(e));
    }
  }

  const inicio = horaNaArena(chamada.inicio, chamada.fuso);
  const descricao = [dataPorExtenso(chamada.data), chamada.quadra.nome, chamada.professor]
    .filter(Boolean)
    .join(' · ');

  return (
    <Modal
      aberto
      onFechar={onFechar}
      largura="estreita"
      titulo={`Chamada · ${chamada.turma.titulo} ${inicio}`}
      descricao={descricao}
      rodape={
        <>
          <span className="left" aria-live="polite">
            {resumo.presentes} presente{resumo.presentes === 1 ? '' : 's'} · {resumo.faltas} falta
            {resumo.faltas === 1 ? '' : 's'}
            {resumo.reposicoes > 0
              ? ` · ${resumo.reposicoes} reposiç${resumo.reposicoes === 1 ? 'ão' : 'ões'}`
              : ''}
          </span>
          <Botao variante="fantasma" onClick={onFechar}>
            {podeRegistrar ? 'Cancelar' : 'Fechar'}
          </Botao>
          {podeRegistrar && (
            <Botao onClick={() => void confirmar()} disabled={salvar.isPending}>
              {salvar.isPending ? 'Salvando…' : 'Salvar chamada'}
            </Botao>
          )}
        </>
      }
    >
      <CorpoDeModal>
        {chamada.cancelada && (
          <div className="aviso conflito">
            <Icone nome="x" />
            <div>
              <b>Aula cancelada</b>
              {chamada.motivoCancelamento ? ` · ${chamada.motivoCancelamento}` : ''}. Aula cancelada
              não tem chamada.
            </div>
          </div>
        )}
        {!chamada.cancelada && !chamada.aberta && (
          <div className="aviso">
            <Icone nome="cal" />
            <div>A chamada abre quando a aula começa, às {inicio}.</div>
          </div>
        )}
        {chamada.registrada && chamada.registradaEm && (
          <p className="sub">
            Chamada registrada às {horaNaArena(chamada.registradaEm, chamada.fuso)}. Salvar de novo
            atualiza o que foi marcado.
          </p>
        )}

        {chamada.alunos.length === 0 && visitantes.length === 0 ? (
          <Vazio>Ninguém matriculado nesta turma na data desta aula.</Vazio>
        ) : (
          <ul className="lista-da-chamada">
            {chamada.alunos.map((linha) => {
              const marca = marcas.get(linha.aluno.id) ?? {
                estado: 'Presente',
                justificada: false,
              };
              return (
                <LinhaDeChamada
                  key={linha.aluno.id}
                  aluno={linha.aluno}
                  marca={marca}
                  opcoes={['Presente', 'Falta']}
                  desabilitada={!podeRegistrar}
                  onMarcar={(estado) => marcar(linha.aluno.id, estado)}
                  onJustificar={(valor) => justificar(linha.aluno.id, valor)}
                />
              );
            })}
            {visitantes.map((aluno) => (
              <LinhaDeChamada
                key={aluno.id}
                aluno={aluno}
                marca={{ estado: 'Reposicao', justificada: false }}
                opcoes={['Reposicao']}
                desabilitada={!podeRegistrar}
                onRemover={() => setVisitantes((atuais) => atuais.filter((v) => v.id !== aluno.id))}
              />
            ))}
          </ul>
        )}

        {podeRegistrar && (
          <BuscaDeReposicao
            marcacaoId={chamada.marcacaoId}
            ignorar={
              new Set([...chamada.alunos.map((l) => l.aluno.id), ...visitantes.map((v) => v.id)])
            }
            onEscolher={(aluno) => setVisitantes((atuais) => [...atuais, aluno])}
          />
        )}

        {erro && (
          <div className="aviso conflito" role="alert">
            <Icone nome="info" />
            <div>{erro}</div>
          </div>
        )}
      </CorpoDeModal>
    </Modal>
  );
}

function LinhaDeChamada({
  aluno,
  marca,
  opcoes,
  desabilitada,
  onMarcar,
  onJustificar,
  onRemover,
}: {
  aluno: LinhaDaChamada['aluno'];
  marca: Marca;
  opcoes: readonly PresencaChamada[];
  desabilitada: boolean;
  onMarcar?: (estado: PresencaChamada) => void;
  onJustificar?: (justificada: boolean) => void;
  onRemover?: () => void;
}) {
  return (
    <li className="linha-da-chamada">
      <Avatar nome={aluno.nome} tamanho={30} />
      <div className="quem-na-chamada">
        <b>{aluno.apelido ?? aluno.nome}</b>
        {aluno.apelido && <small>{aluno.nome}</small>}
        {marca.estado === 'Falta' && onJustificar && (
          <label className="justificada">
            <input
              type="checkbox"
              checked={marca.justificada}
              disabled={desabilitada}
              onChange={(e) => onJustificar(e.target.checked)}
            />
            Falta justificada · dá direito a reposição
          </label>
        )}
      </div>
      <span className="botoes-da-chamada" role="group" aria-label={`Chamada de ${aluno.nome}`}>
        {opcoes.map((estado) => {
          const pressionado = marca.estado === estado;
          return (
            <button
              key={estado}
              type="button"
              className="choice"
              aria-pressed={pressionado}
              disabled={desabilitada || !onMarcar}
              style={pressionado ? { borderColor: COR_DO_ESTADO[estado] } : undefined}
              onClick={() => onMarcar?.(estado)}
            >
              {ROTULO_ESTADO_NA_CHAMADA[estado]}
            </button>
          );
        })}
        {onRemover && !desabilitada && (
          <button
            type="button"
            className="icon-btn"
            aria-label={`Tirar ${aluno.nome} da chamada`}
            onClick={onRemover}
          >
            <Icone nome="x" />
          </button>
        )}
      </span>
    </li>
  );
}

/**
 * Quem veio repor: busca por trecho do nome, só entre quem tem saldo de
 * reposição e não é da turma no dia. Três letras no mínimo — a busca não
 * despeja a arena na tela do professor.
 */
function BuscaDeReposicao({
  marcacaoId,
  ignorar,
  onEscolher,
}: {
  marcacaoId: string;
  ignorar: ReadonlySet<string>;
  onEscolher: (aluno: LinhaDaChamada['aluno']) => void;
}) {
  const [aberta, setAberta] = useState(false);
  const [busca, setBusca] = useState('');
  const termo = useAtrasado(busca.trim(), 300);

  const candidatos = useQuery({
    queryKey: ['turmas', 'reposicao', marcacaoId, termo],
    queryFn: () => turmasApi.candidatosAReposicao(marcacaoId, termo),
    enabled: aberta && termo.length >= 3,
    retry: false,
  });

  if (!aberta) {
    return (
      <Botao variante="contorno" pequeno icone="plus" onClick={() => setAberta(true)}>
        Alguém veio repor aula
      </Botao>
    );
  }

  const lista = (candidatos.data ?? []).filter((c) => !ignorar.has(c.aluno.id));

  return (
    <div className="field">
      <label htmlFor="busca-reposicao">Quem veio repor</label>
      <input
        id="busca-reposicao"
        autoFocus
        placeholder="Digite parte do nome"
        value={busca}
        onChange={(e) => setBusca(e.target.value)}
      />
      <div className="help">Só aparece quem tem reposição disponível.</div>
      {termo.length >= 3 && candidatos.data && lista.length === 0 && (
        <div className="help">Ninguém com reposição disponível por esse nome.</div>
      )}
      {candidatos.isError && <div className="help">{mensagemDoErro(candidatos.error)}</div>}
      {lista.length > 0 && (
        <ul className="feed">
          {lista.map((c) => (
            <li
              key={c.aluno.id}
              className="clickable"
              role="button"
              tabIndex={0}
              onClick={() => {
                onEscolher(c.aluno);
                setBusca('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onEscolher(c.aluno);
                  setBusca('');
                }
              }}
            >
              <Avatar nome={c.aluno.nome} />
              <div>
                <b>{c.aluno.nome}</b>
                <small>
                  {c.saldo} reposiç{c.saldo === 1 ? 'ão disponível' : 'ões disponíveis'}
                </small>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
